import { useFlags } from "launchdarkly-react-client-sdk";

import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import { lazyNamed, lazyWithRetry } from "src/utils/lazy-named";
import { DrawerRouteGuard } from "src/cep/shared/drawers/drawer-route-guard";
import { getMetadataDrawerMode } from "./editability";
import { prefetchContentBlocks } from "./hooks/prefetch-content-blocks";

const MessagesLoader = lazyWithRetry(() => import("./messages"));
const MessageDetailLoader = lazyWithRetry(() => import("./message-detail"));
const MessageOverviewPage = lazyNamed(
  () => import("./message/message-overview-page"),
  "MessageOverviewPage",
);
// All message-detail drawers funnel through a single barrel so Vite/Rollup
// emits one shared chunk. The message-detail page also statically imports the
// barrel, making the drawer chunk a preload of the page chunk — by the time
// the user clicks any drawer trigger, the code is already in memory.
const drawers = () => import("./message/drawers");
const MetadataDrawer = lazyNamed(drawers, "MetadataDrawer");
const SendLimitsDrawer = lazyNamed(drawers, "SendLimitsDrawer");

const MessageContentSurface = lazyNamed(
  () => import("./message/message-content-surface"),
  "MessageContentSurface",
);

// With labels on, journey-triggered messages live in the unified campaigns
// list, so the standalone Messages list is retired — a direct visit redirects
// there. The message-detail routes below stay (they are the editor).
// FeatureFlagRoute redirects when a flag is off; this gate is the inverse
// (redirect while the flag is on), so it stays a small inline component.
const MessagesListRoute = () => {
  const { cepLabels } = useFlags();
  return cepLabels ? (
    <Navigate to="/native-delivery/campaigns" replace />
  ) : (
    <MessagesLoader />
  );
};

export const routes: RouteObject[] = [
  {
    path: "native-delivery/messages",
    spacesAccess: "hidden-from-limited-view",
    element: <MessagesListRoute />,
  },
  {
    path: "native-delivery/messages/:messageId",
    spacesAccess: "hidden-from-limited-view",
    element: <MessageDetailLoader />,
    loader: prefetchContentBlocks,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="overview" replace />,
      },
      {
        path: "overview",
        spacesAccess: "hidden-from-limited-view",
        element: <MessageOverviewPage />,
        children: [
          {
            path: "metadata",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard
                isAllowed={(s) =>
                  getMetadataDrawerMode(s, s.message.channel_type) != null
                }
              >
                <MetadataDrawer />
              </DrawerRouteGuard>
            ),
          },
          {
            path: "content",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard>
                <MessageContentSurface />
              </DrawerRouteGuard>
            ),
          },
          {
            // Parity with the campaign host, so a link to a message's Localize
            // tab resolves from either page. Sibling, so `..` reaches overview.
            path: "content/localize",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard>
                <MessageContentSurface initialTab="localize" />
              </DrawerRouteGuard>
            ),
          },
          {
            path: "send-limits",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <DrawerRouteGuard isAllowed={(s) => s.canEditStructure}>
                <SendLimitsDrawer />
              </DrawerRouteGuard>
            ),
          },
        ],
      },
    ],
  },
];
